import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { RiRestartLine } from '@remixicon/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type Channel as StreamChannel } from 'stream-chat';
import {
	Channel,
	ChannelHeaderProps,
	Chat,
	MessageInput,
	useChannelStateContext,
	useCreateChatClient,
	useMessageContext,
	useMessageInputContext,
	VirtualizedMessageList,
	Window,
} from 'stream-chat-react';
import { useEnrollContext } from '../../contexts/Enroll/EnrollContext';
import { useApi } from '../../hooks/useApi';
import { useModalLoginStore } from '../../store/modalLogin';
import { useUserProfileStore, useUserStore } from '../../store/user';
import { Emoji } from '../Emoji';
import { Send } from '../Icons/Send';

type LiveChatProps = {
	tournamentId: string;
	token: string | null;
};
const APP_ENV = process.env.REACT_APP_ENV;
const apiKey =
	APP_ENV === 'DEV' || APP_ENV === 'STG' ? 't9zqjxm32wac' : 'efe24uu7hbbc';

export const LiveChat = ({ tournamentId, token }: LiveChatProps) => {
	const api = useApi();
	const { openTab } = useEnrollContext();
	const { openModalLogin } = useModalLoginStore();
	const userInfo = useUserProfileStore((state: any) => state.userData);
	const user = useUserStore((state) => state.user);
	const [channel, setChannel] = useState<StreamChannel | null>(null);
	const inputCustomRef = useRef<HTMLTextAreaElement>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	const tokenProvider = useCallback(async () => {
		const response = await api.getTokenFeed();
		if (!response?.success) {
			throw new Error(response.message);
		}
		const { token } = response;
		return token;
	}, []);

	const client = useCreateChatClient({
		apiKey,
		tokenOrProvider: token ? token : tokenProvider,
		userData: {
			id: user ? user?.accountId : 'anonymous',
			name: user ? userInfo.nick : 'anonymous',
			image: `https://getstream.io/random_png/?name=${
				user ? userInfo.nick : 'anonymous'
			}`,
			language: 'pt',
		},
	});

	useEffect(() => {
		if (openTab === 'Chat ao vivo') {
			if (!client) return;

			const connectUser = async () => {
				if (user) {
					await client.disconnect();
					await client.connectUser(
						{
							id: user.accountId,
							name: userInfo.nick,
							image: `https://getstream.io/random_png/?name=${userInfo.nick}`,
						},
						token
					);
				} else {
					await client.disconnect();
					await client.connectUser(
						{
							id: 'anonymous',
							name: 'anonymous',
							image: `https://getstream.io/random_png/?name=anonymous`,
							anon: true,
						},
						tokenProvider
					);
				}
			};

			connectUser()
				.then(() => {
					document
						.getElementById('launcher')
						?.classList.add('hidden');
					const newChannel = client.channel(
						'chatTournament',
						tournamentId,
						{
							image: 'https://getstream.io/random_png/?name=react',
							name: 'Torneio',
						}
					);
					newChannel.watch();
					setChannel(newChannel);
					setIsLoading(false);
				})
				.catch((err) => {
					console.error('Error connecting user:', err);
				});

			return () => {
				if (channel) {
					setChannel(null);
					document
						.getElementById('launcher')
						?.classList.remove('hidden');
				}
			};
		} else {
			if (channel) {
				setChannel(null);
				document.getElementById('launcher')?.classList.remove('hidden');
			}
		}
	}, [client, tournamentId, user, token, openTab]);

	useEffect(() => {
		const chatContainer = chatContainerRef.current;
		if (chatContainer) {
			chatContainer.scrollTop = chatContainer.scrollHeight;
		}
	}, [channel?.id]);

	const CustomMessage = () => {
		const { message } = useMessageContext();

		return (
			<div className="flex flex-row bottom-0 justify-start items-start gap-2 py-1 px-5">
				<div className="text-left text-black text-sm font-semibold font-bahnschrift leading-none tracking-tight">
					{message.user?.name}
				</div>
				<div className="text-justify text-black text-sm font-light font-bahnschrift leading-none tracking-tight w-4/5">
					{message.text ===
					'Message was blocked by moderation policies'
						? 'A mensagem foi bloqueada pelas políticas de moderação!'
						: message.text}
				</div>
			</div>
		);
	};

	const CustomInput = () => {
		const { text, handleChange, handleSubmit } = useMessageInputContext();

		const handleKeyPress = (
			event: React.KeyboardEvent<HTMLTextAreaElement>
		) => {
			if (!user) {
				openModalLogin(true, '');
				return;
			}
			if (event.key === 'Enter' && !event.shiftKey) {
				event.preventDefault();
				handleSubmit();
				Keyboard.hide();
				// if (inputCustomRef?.current) {
				// 	inputCustomRef.current.style.height = `40px`;
				// 	inputCustomRef?.current?.focus();
				// }
			}
		};

		const autoResize = (e: any) => {
			if (inputCustomRef.current) {
				if (e.target.value === '') {
					inputCustomRef.current.style.height = `auto`;
				} else {
					inputCustomRef.current.style.height = 'auto';
					const newHeight = Math.min(
						inputCustomRef.current.scrollHeight,
						250
					);
					inputCustomRef.current.style.height = `${newHeight}px`;
				}
			}
		};

		return (
			<div
				className={
					'w-full left-0 fixed px-4 pt-2 bg-[#e3e3e3] border-t border-[#9b9b9b] flex-col justify-center items-center inline-flex' +
					(Capacitor.getPlatform() === 'ios'
						? ' bottom-[65px]'
						: ' bottom-[50px]')
				}
			>
				<div className="self-stretch grow shrink basis-0 pt-0.5 justify-center items-center gap-2.5 inline-flex">
					<div className="grow shrink basis-0 px-3 bg-white rounded-2xl border border-[#c9c4bb] justify-between items-center flex">
						<textarea
							ref={inputCustomRef}
							className="w-full  text-black pt-3 text-base font-light leading-tight font-bahnschrift tracking-tight outline-none resize-none placeholder:text-left"
							placeholder="Digite aqui sua mensagem..."
							value={text}
							onChange={(e) => {
								handleChange(e);
								autoResize(e);
							}}
							onKeyDown={handleKeyPress}
							style={{
								height: '48px',
								minHeight: '48px',
								maxHeight: '250px',
								overflowY: 'auto',
							}}
						/>
						<Emoji />
					</div>

					<div
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleSubmit();
							Keyboard.hide();
							// if (inputCustomRef?.current) {
							// 	inputCustomRef.current.style.height = `40px`;
							// 	inputCustomRef?.current?.focus();
							// }
						}}
						className="w-9 h-6 cursor-pointer"
					>
						<Send />
					</div>
				</div>

				<div className="grow shrink basis-0 px-32 py-2 flex-col justify-end items-center gap-2 flex"></div>
			</div>
		);
	};

	const CustomHeader = (props: ChannelHeaderProps) => {
		const { watcher_count } = useChannelStateContext();
		return (
			<div className="px-3">
				<div className="w-full h-8 px-4 py-2 bg-[#d0d0d0] justify-start items-center gap-1 inline-flex">
					<div className="flex-col justify-start items-start gap-1 inline-flex">
						<div className="text-center text-[#484848] text-sm font-bahnschrift leading-none tracking-tight">
							{watcher_count}{' '}
							{watcher_count && watcher_count > 1
								? 'participantes'
								: 'participante'}{' '}
							online
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="flex items-center w-full gap-4">
			{isLoading ? (
				<div className="flex items-center justify-center">
					<RiRestartLine className="mr-2 h-4 w-4 animate-spin" />
				</div>
			) : (
				client && (
					<div className="w-full h-auto overflow-y-auto">
						<Chat client={client} defaultLanguage="pt">
							<Channel
								LoadingIndicator={() => (
									<div className="flex items-center justify-center">
										<RiRestartLine className="mr-2 h-4 w-4 animate-spin" />
									</div>
								)}
								channel={channel!}
								Message={CustomMessage}
								Input={CustomInput}
							>
								<Window>
									<CustomHeader />
									<div className="flex flex-col">
										<div className="fex-1 mb-24 overflow-y-auto">
											<VirtualizedMessageList
												hideDeletedMessages
												scrollToLatestMessageOnFocus
												hideNewMessageSeparator
												disableDateSeparator
												showUnreadNotificationAlways
												Message={CustomMessage}
												additionalVirtuosoProps={{
													style: {
														paddingTop: '8px',
														paddingBottom: '8px',
														// height: '10vh',
														minHeight: '18vh',
														maxHeight: '35vh',
													},
													useWindowScroll: false,
													followOutput: true,
													overscan: 10,
												}}
											/>
										</div>
									</div>

									<MessageInput focus />
								</Window>
							</Channel>
						</Chat>
					</div>
				)
			)}
		</div>
	);
};
